import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Text,
  Select,
  Button,
  Heading,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useDisclosure,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { PDFViewer } from "@react-pdf/renderer";
import Card from "components/card/Card.js";
import { GetInstitution } from "services/institutionService";
import { DownloadablePDF, generateDownloadLink } from "helpers/certificate";
import { FaFilePdf } from "react-icons/fa";

export default function ManualReport() {
  const toast = useToast();
  const userState = useSelector((state) => state?.user?.value);
  const token = userState.hasOwnProperty("token") ? userState.token : null;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [institutions, setInstitutions] = useState([]);

  const [manualData, setManualData] = useState({
    institutionId: "",
    otherInstitutionName: "",
    candidateName: "",
    course: "",
    degree: "",
    grade: "",
    year: "",
  });

  const [pdfData, setPdfData] = useState(null);

  const getInstitutions = async () => {
    try {
      const response = await GetInstitution(token);
      if (response.success) {
        setInstitutions(response?.data);
      }
    } catch (error) {
      toast({
        id: error.message || "api-error",
        title: error.message || "Failed to fetch institutions",
        status: "error",
        duration: 5000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getInstitutions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setManualData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePreview = () => {
    if (!manualData.institutionId || !manualData.candidateName) {
      toast({
        title: "Please select an institution and enter the candidate's name.",
        status: "warning",
        duration: 5000,
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    if (manualData.institutionId === "others" && !manualData.otherInstitutionName.trim()) {
      toast({
        title: "Please enter the custom institution name.",
        status: "warning",
        duration: 5000,
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    const selectedInstitution = institutions.find(
      (inst) => inst._id === manualData.institutionId
    );

    const institutionName = manualData.institutionId === "others" 
      ? manualData.otherInstitutionName 
      : (selectedInstitution ? selectedInstitution.institutionName : "N/A");

    const certificateMock = {
      _doc: {
        name: manualData.candidateName,
        course: manualData.course || "N/A",
        degree: manualData.degree || "N/A",
        grade: manualData.grade || "N/A",
        year: manualData.year || "N/A",
      },
      institution: {
        institutionName: institutionName,
      },
    };

    setPdfData(certificateMock);
    onOpen();
  };

  const handleDownload = async () => {
    if (!pdfData) return;
    try {
      setDownloadLoading(true);
      const pdfNode = <DownloadablePDF certificate={pdfData} />;
      const isDownloaded = await generateDownloadLink(pdfNode, pdfData._doc.name);
      setDownloadLoading(false);

      if (isDownloaded) {
        toast({
          title: "Report Downloaded Successfully",
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to download report",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        id: error.message || "api-error",
        title: error.message || "An error occurred",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <Modal onClose={onClose} size={"2xl"} isOpen={isOpen}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {pdfData?._doc?.name ? `${pdfData._doc.name} - Manual Report Preview` : "Report Preview"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody minH={"600px"}>
            <Box style={{ width: "100%", height: "600px" }}>
              {pdfData && (
                <PDFViewer width="100%" height="100%">
                  <DownloadablePDF certificate={pdfData} />
                </PDFViewer>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
             <Button colorScheme='gray' mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              isLoading={downloadLoading}
              loadingText="Wait..."
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box mt={{ sm: "40px", md: "100px" }} mb="20">
        <SimpleGrid columns={{ sm: 1, md: 2 }} w="100%" p="2" spacing="2" mb="4">
          <Box mb="5">
            <Text
              color={"black"}
              mb="4px"
              fontWeight="bold"
              fontSize={{ base: "md", sm: "lg", md: "xl", lg: "2xl" }}
            >
              Generate Manual Report
            </Text>
            <Text color={"gray.500"} fontSize="md">
              Manually fill out candidate information to instantly preview and download a formatting-compliant credential report.
            </Text>
          </Box>
        </SimpleGrid>

        <Card bg="gray.100">
          <SimpleGrid width="100%" columns={{ base: 1, md: 2 }} spacing="6" mb="15" mt="5">
            
            <Box>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">School/Institution</FormLabel>
                <Select
                  bg="white"
                  variant="main"
                  placeholder="Select School/Institution"
                  name="institutionId"
                  value={manualData.institutionId}
                  onChange={handleChange}
                >
                  {institutions.length > 0 &&
                    institutions.map((inst) => (
                      <option key={inst._id} value={inst._id}>
                        {inst.institutionName}
                      </option>
                    ))}
                  <option value="others">Others</option>
                </Select>
              </FormControl>
            </Box>

            {manualData.institutionId === "others" && (
              <Box>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Other Institution Name</FormLabel>
                  <Input
                    bg="white"
                    variant="main"
                    name="otherInstitutionName"
                    placeholder="e.g. Harvard University"
                    value={manualData.otherInstitutionName}
                    onChange={handleChange}
                  />
                </FormControl>
              </Box>
            )}

            <Box>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Candidate's Full Name</FormLabel>
                <Input
                  bg="white"
                  variant="main"
                  name="candidateName"
                  placeholder="e.g. John Doe Oluwafemi"
                  value={manualData.candidateName}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
            
            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">Course Title</FormLabel>
                <Input
                  bg="white"
                  variant="main"
                  name="course"
                  placeholder="e.g. Computer Science"
                  value={manualData.course}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">Degree</FormLabel>
                <Input
                  bg="white"
                  variant="main"
                  name="degree"
                  placeholder="e.g. B.Sc."
                  value={manualData.degree}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">Grade / Class of Degree</FormLabel>
                <Input
                  bg="white"
                  variant="main"
                  name="grade"
                  placeholder="e.g. First Class Honours"
                  value={manualData.grade}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">Graduation Year</FormLabel>
                <Input
                  bg="white"
                  variant="main"
                  name="year"
                  placeholder="e.g. 2021 or 2019/2020"
                  value={manualData.year}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>

          </SimpleGrid>

          <Box mt="10" textAlign="right">
            <Button
              variant="brand"
              width="200px"
              onClick={handlePreview}
            >
              <Icon as={FaFilePdf} mr={2} /> Preview Report
            </Button>
          </Box>
        </Card>
      </Box>
    </>
  );
}
